import React, { useState } from 'react'
import ReportLayout from '../../layouts/Report'

import useGetSchools from '../../../hooks/schools/useGetSchools'
import useGetCampuses from '../../../hooks/campuses/useGetCampuses'
import useGetActiveUserReport from '../../../hooks/analytics/useGetActiveUsersReport'
import useExportActiveUserReport from '../../../hooks/analytics/useExportActiveUserReport'

import LineChart from '../../sub_components/charts/LineChart'
import ReportTablet from '../../sub_components/ReportTablet'
import ReportDatePicker from '../../sub_components/ReportDatePicker'
import FilterTable from '../../sub_components/FilterTable'

import { getDateString } from '../../../helpers/utility'

import { IColumnProps, IFilterableDataProps } from '../../../interfaces/table'


const ActiveUserReport = () => {
    const [schoolFilters, setSchoolFilters] = useState<number[]>([]);
    const [campusFilters, setCampusFilters] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const filterQuery = new URLSearchParams();
    filterQuery.set("start_date", startDate);
    filterQuery.set("end_date", endDate);
    schoolFilters.forEach((id) => filterQuery.append("school_ids", id.toString()));
    campusFilters.forEach((id) => filterQuery.append("campus_ids", id.toString()));

    const { data: schools } = useGetSchools();
    const { data: campuses } = useGetCampuses();
    const { data: activeUserReport } = useGetActiveUserReport(filterQuery.toString());

    const { mutate: exportCsv } = useExportActiveUserReport();

    let filterTableDataColumns__Schools: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "School Name", identifier: "name" },
    ];
    let filterTableDataColumns__Campuses: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Campus Name", identifier: "name" },
    ];
    let filterTableDataColumns__ActiveUserReport: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Full Name", identifier: "full_name" },
        { name: "Email", identifier: "email" },
        { name: "Last Active", identifier: "login_at" },
    ];

    let filterTableData__Schools: IFilterableDataProps[] = [];
    let filterTableData__Campuses: IFilterableDataProps[] = [];
    let filterTableData__ActiveUserReport: IFilterableDataProps[] = [];

    if (schools) {
        filterTableData__Schools = schools.map((school) => ({
            id: school.id,
            name: school.name,
        }));
    }

    if (campuses) {
        filterTableData__Campuses = campuses.map((campus) => ({
            id: campus.id,
            name: campus.name,
        }));
    }

    if (activeUserReport) {
        filterTableData__ActiveUserReport = activeUserReport.table_data.map((user) => ({
            id: user.id,
            full_name: user.full_name,
            email: user.email,
            login_at: getDateString(user.login_at),
        }));
    }

    const onExportCsvReport = () => {
        exportCsv({
            payload: {
                filterQuery: filterQuery.toString(),
            }
        });
    }

    return (
        <ReportLayout>
            <div className='immersion_admin__careers_report'>
                <div className='immersion_admin__careers_report__header'>
                    <div className='immersion_admin__careers_report__header__content'>
                        <h2 className='immersion_admin__careers_report__header__content__title'>Active User Report</h2>
                        <p className='immersion_admin__careers_report__header__content__description'>
                            This slide gives an overview of users who have logged in and had activity on the platform since the start of data collection (20/04/24).
                            The chart to the bottom left shows a cumulative timeline of active users in Immersion (defined as above, having logged into the platform at least once).
                            The table to the bottom right shows the latest date a user has had activity on the platform.
                        </p>
                    </div>
                    <div className='immersion_admin__careers_report__header__actions'>
                        <button className='immersion_admin__btn__primary immersion_admin__careers_report__header__actions__button' onClick={onExportCsvReport}>Export CSV</button>
                    </div>
                </div>

                <div className='immersion_admin__careers_report__chart_body'>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__Schools}
                            data={filterTableData__Schools}
                            enableFilters={true}
                            filters={schoolFilters}
                            setFilters={setSchoolFilters}
                        />
                        <FilterTable
                            columns={filterTableDataColumns__Campuses}
                            data={filterTableData__Campuses}
                            enableFilters={true}
                            filters={campusFilters}
                            setFilters={setCampusFilters}
                        />
                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <ReportDatePicker
                                required={true}
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                            />
                            <div className='immersion_admin__careers_report__chart__row'>
                                <ReportTablet
                                    title={activeUserReport?.active_accounts_in_period?.toString() ?? "0"}
                                    description='Active Accounts in Period'
                                    color={'green'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='immersion_admin__careers_report__chart__row'>
                        {activeUserReport?.active_accounts_chart_data.length ? (
                            <LineChart
                                width={600}
                                height={318}
                                data={activeUserReport?.active_accounts_chart_data ?? []}
                                xLabel=""
                                yLabel="Active Accounts"
                            />
                        ) : (
                            <div className='immersion_admin__careers_report__chart__no_data'>
                                No analytics data available
                            </div>
                        )}

                        <FilterTable
                            columns={filterTableDataColumns__ActiveUserReport}
                            data={filterTableData__ActiveUserReport}
                            enableFilters={false}
                            filters={[]}
                            setFilters={() => { }}
                        />
                    </div>
                </div>
            </div>
        </ReportLayout>
    )
}

export default ActiveUserReport;